import React, { useState } from "react";
import {
  Box,
  IconButton,
  MenuItem,
  Popover,
  Select,
  Typography,
} from "@mui/material";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FormatLineSpacingOutlinedIcon from "@mui/icons-material/FormatLineSpacingOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

interface TableHeaderCellProps {
  label: string;
  columnKey: string;

  sortable?: boolean;
  sortOrder?: "asc" | "desc" | null;
  onSort?: (columnKey: string) => void;

  filter?: {
    value: string;
    options: string[];
    onChange: (value: string) => void;
  };
}

const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  label,
  columnKey,
  sortable,
  sortOrder,
  onSort,
  filter,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        width: "100%",
      }}
    >
      {/* LEFT (empty – keeps label centered) */}
      <Box />

      {/* CENTER LABEL */}
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 600,
          textAlign: "center",
          whiteSpace: "nowrap",
          pointerEvents: "none",
          color: "grey.800",
        }}
      >
        {label}
      </Typography>

      {/* RIGHT ICONS */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: 0.5,
          pr: 0.5,
        }}
      >
        {/* SORT ICON */}
        {sortable && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onSort?.(columnKey);
            }}
            sx={{
              p: "4px",
              color: "grey.600",
              "&:hover": { color: "primary.main" },
            }}
          >
            {sortOrder === "asc" ? (
              <ArrowUpwardIcon fontSize="small" />
            ) : sortOrder === "desc" ? (
              <ArrowDownwardIcon fontSize="small" />
            ) : (
              <FormatLineSpacingOutlinedIcon fontSize="small" />
            )}
          </IconButton>
        )}

        {/* FILTER ICON */}
        {filter && (
          <>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setAnchorEl(e.currentTarget);
              }}
              sx={{
                p: "4px",
                color: filter.value ? "primary.main" : "grey.600",
                "&:hover": { color: "primary.main" },
              }}
            >
              <FilterListOutlinedIcon fontSize="small" />
            </IconButton>

            {/* Popover unchanged */}
            <Popover
              open={Boolean(anchorEl)}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { p: 2, minWidth: 180 } }}
            >
              <Typography fontSize={12} fontWeight={600} mb={1}>
                {label}
              </Typography>

              <Select
                fullWidth
                size="small"
                value={filter.value}
                onChange={(e) => {
                  filter.onChange(e.target.value);
                  setAnchorEl(null);
                }}
              >
                <MenuItem value="">All</MenuItem>
                {filter.options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </Select>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
};

export default TableHeaderCell;
